import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';
import styles from './PriceRangeFilter.module.scss';
import i18n from '@/settings/i18n';
import useIsomorphicLayoutEffect from '@/lib/use-iso-layout-effects';
import { PriceRangeFilterProps } from './types';

const t = i18n.init();

export default function PriceRangeFilter(props: PriceRangeFilterProps) {
  const { range, values, isLoading } = props;
  const [min, setMin] = useState(values.min || range.min); // we use _min/_max because it can happen the the query string is out of range, provinding some weird UI.
  const [max, setMax] = useState(values.max || range.max);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedOnChange = useMemo(
    () =>
      debounce((newValues: { min: number; max: number }) => {
        props.onChange(newValues);
      }, 1500),
    [props.onChange],
  );

  const handleMinChange = (value: number) => {
    if (value >= 0 && value < range.max) setMin(value);
    if (value < max && value > range.min)
      debouncedOnChange({ min: value, max });
  };

  const handleMaxChange = (value: number) => {
    if (value <= range.max) setMax(value);

    if (min < value && value <= range.max)
      debouncedOnChange({ min, max: value });
  };

  useEffect(() => {
    const minValue = values.min || range.min;

    if (range.max >= minValue && minValue >= range.min) setMin(minValue);
  }, [values.min]);

  useEffect(() => {
    const maxValue = values.max || range.max;

    if (range.min <= maxValue && maxValue <= range.max) setMax(maxValue);
  }, [values.max]);

  useIsomorphicLayoutEffect(() => {
    // align min/max whith layout
    const wrapper = containerRef.current;

    if (wrapper) {
      const wrapperSlide = wrapper?.getElementsByTagName('span')[0];

      if (wrapper == null) return;
      wrapper.style.setProperty('--a', min.toString());
      wrapper.style.setProperty('--b', max.toString());
      wrapper.style.setProperty('--min', range.min.toString());
      wrapper.style.setProperty('--max', range.max.toString());

      const rangeDiff = range.max - range.min;
      const left = ((min - range.min) / rangeDiff) * 100;
      const right = 100 - ((max - range.min) / rangeDiff) * 100;

      wrapperSlide?.style.setProperty('left', `${left}%`);
      wrapperSlide?.style.setProperty('right', `${right}%`);
    }
  }, [min, max, range, containerRef]);

  return (
    <div>
      <h2 className="mb-2 text-base font-bold capitalize text-gray-700 xs:mb-3 lg:text-sm lg:uppercase">
        {t`Price range`}
      </h2>
      <div
        className={cn('price-span grid grid-cols-2 gap-2', {
          'pointer-events-none opacity-30': isLoading,
        })}
      >
        <input
          className="rounded bg-gray-200 py-2 px-3 text-base text-gray-700 xs:py-3.5 xl:text-sm"
          value={min}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          disabled={isLoading}
        />
        <input
          className="rounded bg-gray-200 py-2 px-3 text-base text-gray-700 xs:py-3.5 xl:text-sm"
          value={max}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          disabled={isLoading}
        />
      </div>
      <div
        ref={containerRef}
        className={cn(
          styles.wrap,
          {
            'pointer-events-none opacity-30': isLoading,
          },
          {
            'opacity-100': !isLoading,
          },
        )}
      >
        <input
          className={`${styles.range} range-min`}
          aria-label="minimum price range"
          type="range"
          min={range.min}
          max={range.max}
          value={min}
          step="1"
          disabled={isLoading}
          onChange={(e) => {
            const value = Number(e.target.value);

            if (value < max) setMin(value);
          }}
          onMouseUp={() => props.onChange({ min, max })} // Only trigger when user releases the drag
          onTouchEnd={() => props.onChange({ min, max })} // For mobile devices
        />
        <input
          type="range"
          aria-label="price range"
          className={`${styles.range}`}
          min={range.min}
          max={range.max}
          value={max}
          step="1"
          disabled={isLoading}
          onChange={(e) => {
            const value = Number(e.target.value);

            if (min < value) setMax(value);
          }}
          onMouseUp={() => props.onChange({ min, max })} // Only trigger when user releases the drag
          onTouchEnd={() => props.onChange({ min, max })} // For mobile devices
        />
        <span className={styles.line} />
      </div>
    </div>
  );
}
